import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { saveCookiesToLocalStorage, getCookiesFromLocalStorage, getCookie } from '../auth/cookies';
import { 
  FaEnvelope, 
  FaLock, 
  FaUser, 
  FaUserPlus, 
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaSpinner,
  FaSignInAlt,
  FaCheck,
  FaShieldAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';

function Register() {
  const location = useLocation();
  const admin = location.state?.admin || false;

  const [formData, setFormData] = useState({
    email: '',
    completeName: '',
    password1: '',
    password2: '',
  });

  const [errors, setErrors] = useState({});
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      const sessionid = getCookie('sessionid');
      if (sessionid) {
        try {
          const response = await axios.get('/api/check_auth/');
          if (response.data.authenticated) {
            navigate('/');
          }
        } catch (error) {
          // If there's an error checking authentication, we'll just continue with the register page
          console.error('Error checking authentication:', error);
        }
      }
    };
    
    checkSession();
  }, [navigate]);

  // Check password strength
  useEffect(() => {
    if (!formData.password1) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    // Length check
    if (formData.password1.length >= 8) strength += 1;
    // Contains lowercase letters
    if (/[a-z]/.test(formData.password1)) strength += 1;
    // Contains uppercase letters
    if (/[A-Z]/.test(formData.password1)) strength += 1;
    // Contains numbers
    if (/[0-9]/.test(formData.password1)) strength += 1;
    // Contains special characters
    if (/[^a-zA-Z0-9]/.test(formData.password1)) strength += 1;

    setPasswordStrength(strength);
  }, [formData.password1]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: []
      });
    }
    
    // Clear password match error when user changes either password field
    if (name === 'password1' || name === 'password2') {
      setPasswordError('');
    }
  };

  const renderPasswordStrength = () => {
    const getStrengthLabel = () => {
      if (passwordStrength === 0) return '';
      if (passwordStrength <= 2) return 'Weak';
      if (passwordStrength <= 4) return 'Medium';
      return 'Strong';
    };

    const getStrengthColor = () => {
      if (passwordStrength === 0) return 'bg-gray-300 dark:bg-gray-600';
      if (passwordStrength <= 2) return 'bg-red-500';
      if (passwordStrength <= 4) return 'bg-yellow-500';
      return 'bg-green-500';
    };

    if (!formData.password1) return null;

    return (
      <div className="mt-1">
        <div className="flex items-center">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
            <div 
              className={`h-2.5 rounded-full ${getStrengthColor()}`} 
              style={{ width: `${(passwordStrength / 5) * 100}%` }}
            ></div>
          </div>
          <span className={`ml-2 text-xs ${
            passwordStrength <= 2 ? 'text-red-500' : 
            passwordStrength <= 4 ? 'text-yellow-500' : 
            'text-green-500'
          }`}>
            {getStrengthLabel()}
          </span>
        </div>
      </div>
    );
  };

  const renderFieldError = (fieldName) => {
    if (!errors[fieldName] || errors[fieldName].length === 0) return null;
    
    return (
      <div className="mt-1">
        {errors[fieldName].map((error, index) => (
          <p key={index} className="text-red-500 text-xs flex items-start mt-1">
            <FaExclamationTriangle className="mr-1 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </p>
        ))}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setErrors({});
    
    // Password match validation
    if (formData.password1 !== formData.password2) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    // Basic password strength validation
    if (passwordStrength <= 2) {
      setPasswordError('Please choose a stronger password');
      return;
    }

    setIsSubmitting(true);

    try {
      if (admin) {
        console.log("Saving cookies to local storage");
        saveCookiesToLocalStorage();
      }

      await axios.post('/api/auth/register/', formData);
      
      toast.success('Registration successful!');
      
      if (admin) {
        getCookiesFromLocalStorage();
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Registration error:', err);
      
      if (err.response?.status === 500) {
        setErrors({ email: ["Email already in use"] });
        toast.error('Email already in use');
      } else if (err.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else if (err.response?.data) {
        setErrors(err.response.data);
        
        // Display first error in toast
        const firstErrorKey = Object.keys(err.response.data)[0];
        if (firstErrorKey && err.response.data[firstErrorKey].length > 0) {
          toast.error(`${firstErrorKey}: ${err.response.data[firstErrorKey][0]}`);
        }
      } else {
        toast.error('An error occurred during registration');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-secondary to-secondary-dark p-6 text-center">
            <h2 className="text-2xl font-bold text-white flex items-center justify-center">
              <FaUserPlus className="mr-2" />
              {admin ? 'Create Admin Account' : 'Create Your Account'}
            </h2>
            <p className="mt-2 text-blue-100">
              {admin ? 'Register as an administrator' : 'Join the hackathon platform'}
            </p>
          </div>
          
          {/* Form */}
          <div className="p-6">
            <form className="space-y-5" onSubmit={handleSubmit}>
              {/* Admin Registration Badge */}
              {admin && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-3 rounded-md mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FaShieldAlt className="h-5 w-5 text-yellow-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-400">
                        You are creating an administrator account with elevated privileges.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    id="email" 
                    name="email" 
                    type="email" 
                    autoComplete="email" 
                    required 
                    className={`block w-full pl-10 pr-3 py-2 border ${
                      errors.email ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-secondary focus:border-secondary`}
                    placeholder="you@example.com" 
                    value={formData.email} 
                    onChange={handleChange} 
                  />
                </div>
                {renderFieldError('email')}
              </div>
              
              {/* Complete Name Field */}
              <div>
                <label htmlFor="completeName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Full Name
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    id="completeName" 
                    name="completeName" 
                    type="text" 
                    autoComplete="name" 
                    required 
                    className={`block w-full pl-10 pr-3 py-2 border ${
                      errors.completeName ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-secondary focus:border-secondary`}
                    placeholder="John Doe" 
                    value={formData.completeName} 
                    onChange={handleChange} 
                  />
                </div>
                {renderFieldError('completeName')}
              </div>
              
              {/* Password Field */}
              <div>
                <label htmlFor="password1" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    id="password1" 
                    name="password1" 
                    type={showPassword1 ? "text" : "password"} 
                    autoComplete="new-password" 
                    required 
                    className={`block w-full pl-10 pr-10 py-2 border ${
                      passwordError || errors.password1 ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-secondary focus:border-secondary`}
                    placeholder="Choose a secure password" 
                    value={formData.password1} 
                    onChange={handleChange} 
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowPassword1(!showPassword1)}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      {showPassword1 ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                {renderPasswordStrength()}
                {renderFieldError('password1')}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Use at least 8 characters with a mix of letters, numbers and symbols
                </p>
              </div>
              
              {/* Confirm Password Field */}
              <div>
                <label htmlFor="password2" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm Password
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaCheck className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    id="password2" 
                    name="password2" 
                    type={showPassword2 ? "text" : "password"} 
                    autoComplete="new-password" 
                    required 
                    className={`block w-full pl-10 pr-10 py-2 border ${
                      passwordError || errors.password2 ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-secondary focus:border-secondary`}
                    placeholder="Confirm your password" 
                    value={formData.password2} 
                    onChange={handleChange} 
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowPassword2(!showPassword2)}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      {showPassword2 ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                {renderFieldError('password2')}
                {passwordError && (
                  <p className="mt-1 text-xs text-red-500 flex items-start">
                    <FaExclamationTriangle className="mr-1 flex-shrink-0 mt-0.5" />
                    {passwordError}
                  </p>
                )}
              </div>
              
              {/* Submit Button */}
              <div className="pt-2">
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-secondary hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      Registering...
                    </>
                  ) : (
                    <>
                      <FaUserPlus className="mr-2" />
                      {admin ? 'Create Admin Account' : 'Sign Up'}
                    </>
                  )}
                </button>
              </div>
            </form>
            
            <div className="mt-6 flex items-center justify-center">
              <div className="text-sm">
                <span className="text-gray-500 dark:text-gray-400 mr-1">
                  Already have an account?
                </span>
                <button 
                  type="button"
                  onClick={() => navigate("/login")}
                  className="font-medium text-secondary hover:text-secondary-dark dark:text-secondary-light dark:hover:text-secondary flex items-center"
                >
                  <FaSignInAlt className="mr-1" />
                  Sign in
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Additional Help */}
        <div className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
          By signing up, you agree to our Terms of Service and Privacy Policy.
        </div>
      </div>
    </div>
  );
}

export default Register;